/**
 * TelemetryChartGroup — the synchronized stacked chart panel.
 *
 * Owns every cross-row concern: the shared X-axis window, brush-zoom +
 * crosshair overlay, hover tooltip popover, range bar wiring, the
 * throttled / boot annotation strips, and the legend. Each metric row
 * is a `<TelemetryRow />` emitting two grid cells (label + chart); a
 * single CSS grid lays them all out so a single absolutely-positioned
 * overlay can hit-test the whole chart column with one pointer
 * handler — no per-row pointer plumbing.
 *
 * Pointer mechanics mirror DeviceStateTimeline.tsx:685–878 (the
 * verified pattern):
 *   - pointerdown arms `maybe-drag` and captures the pointer
 *   - pointermove past a 3 px threshold flips to `dragging` and shows
 *     the brush rect (crosshair hides during drag)
 *   - pointermove under threshold writes crosshair + tooltip via refs
 *     so per-pixel moves don't trigger React renders
 *   - pointerup either commits a brushed window or does nothing (no
 *     marker hit-tests yet — boot ticks open a popover via their own
 *     click handlers)
 *   - pointerleave aborts drag, hides crosshair
 *
 * The hover state lives in a small piece of React state — only the
 * tooltip's text content reflows on hover, and that subtree is
 * cheap. Pointer position (crosshair x, tooltip x) goes through refs.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useDeviceStateHistory,
  useDeviceTelemetry,
  type StateHistoryEvent,
  type TelemetryPoint,
} from '../../api/hooks';
import { useNow } from '../format';
import { TelemetryRangeBar } from './TelemetryRangeBar';
import { TelemetryRow } from './TelemetryRow';
import {
  TELEMETRY_SECTIONS,
  TELEMETRY_SERIES,
  type TelemetrySection,
  type TelemetrySeries,
} from './series';
import { useTelemetryUrlState } from './useTelemetryUrlState';

const ROW_HEIGHT = 72;
const STRIP_HEIGHT = 16;
const LABEL_COL_PX = 108;
const YTICK_COL_PX = 44;
const GRID_GAP_PX = 8;
const DRAG_THRESHOLD_PX = 3;
const VIEWBOX_W = 100;
// Horizontal offset from the chart-group container's left edge to the
// left edge of the chart cells. Both the pointer overlay and the
// hover-tooltip math depend on this — keep it derived from the grid
// constants above.
const CHART_COL_OFFSET_PX =
  LABEL_COL_PX + GRID_GAP_PX + YTICK_COL_PX + GRID_GAP_PX;

// Local x-axis tick formatter — mirrors DeviceStateTimeline.tsx:283–292
// so axes line up visually between the two views.
function fmtAxisTick(ms: number, hours: number): string {
  const d = new Date(ms);
  if (hours <= 36) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (hours <= 24 * 14) {
    return d.toLocaleDateString([], { weekday: 'short', day: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: '2-digit' });
}

// Window-edge display: dense ISO-like format that fits both ends of the
// range banner without truncation.
function fmtWindowEdge(ms: number, hours: number): string {
  const d = new Date(ms);
  if (hours <= 36) {
    return d.toLocaleString([], {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return d.toLocaleDateString([], {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

function fmtOffsetAgo(deltaMs: number): string {
  const abs = Math.abs(deltaMs);
  if (abs < 60_000) return `${Math.round(abs / 1000)} s`;
  if (abs < 3_600_000) return `${Math.round(abs / 60_000)} min`;
  if (abs < 86_400_000) return `${Math.round(abs / 3_600_000)} h`;
  return `${Math.round(abs / 86_400_000)} d`;
}

interface TelemetryChartGroupProps {
  thing: string;
}

export function TelemetryChartGroup({ thing }: TelemetryChartGroupProps) {
  const {
    rangeKey,
    spanMs,
    centreMs,
    bucket,
    hidden,
    setRangeKey,
    setCentreMs,
    setCustomWindow,
    resetCustom,
  } = useTelemetryUrlState();
  const isCustomSpan = rangeKey === 'custom';

  // Bucket nowMs to the minute so React Query's key doesn't change every
  // re-render (which would re-fire fetches needlessly). useNow ticks once
  // per minute to keep relative-time labels current.
  useNow(60_000);
  const nowMs = Math.floor(Date.now() / 60_000) * 60_000;
  const halfSpanMs = spanMs / 2;
  const isLive = centreMs === null;
  const untilMs = isLive ? nowMs : centreMs + halfSpanMs;
  const sinceMs = untilMs - spanMs;

  // The /devices/:id/telemetry endpoint takes "last N hours from now",
  // not an arbitrary (since, until) window. So fetch enough hours to
  // cover [sinceMs, nowMs]; the chart's X-axis math handles the rest.
  const hoursToFetch = Math.max(
    1,
    Math.ceil((nowMs - sinceMs) / 3_600_000),
  );
  const telemetryQ = useDeviceTelemetry(thing, bucket, hoursToFetch);
  // Boot events come from the same state-history endpoint the state
  // timeline already uses, so two views of the same device read
  // consistent data.
  const stateHistoryQ = useDeviceStateHistory(
    thing,
    new Date(sinceMs).toISOString(),
    new Date(untilMs).toISOString(),
  );

  const allPoints: TelemetryPoint[] = telemetryQ.data?.points ?? [];
  // Filter to the visible window so polylines don't render outside the
  // chart and so the throttled / boot annotation strips reflect only
  // what's in view.
  const points = useMemo(
    () =>
      allPoints.filter((p) => {
        const ts = Date.parse(p.bucket);
        return ts >= sinceMs && ts <= untilMs;
      }),
    [allPoints, sinceMs, untilMs],
  );

  const events = stateHistoryQ.data?.events ?? [];
  const bootMarkers = useMemo(
    () => events.filter((e) => e.event_type === 'boot'),
    [events],
  );

  // Visible-series list, in the registry's canonical order (Host →
  // Network → Sensors → Projector). Filtering here means the row stack,
  // tooltip rows, and legend all stay in sync.
  const visibleSeries = useMemo(
    () => TELEMETRY_SERIES.filter((s) => !hidden.has(s.id)),
    [hidden],
  );

  // Refs for the pointer-driven overlay. The chart container measures
  // its own width via ResizeObserver — we need true pixels for tooltip
  // placement and hit-test math even though the SVG renders in
  // percent-space.
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const crosshairRef = useRef<SVGLineElement | null>(null);
  const brushRectRef = useRef<SVGRectElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  type DragState = 'idle' | 'maybe-drag' | 'dragging';
  const dragStateRef = useRef<DragState>('idle');
  const pointerDownXRef = useRef<number>(0);

  type HoverContent = {
    tMs: number;
    perSeries: Array<{ series: TelemetrySeries; value: number | null }>;
    throttled: boolean;
    nearestBoot: { ts: number; fw: string; deltaMs: number } | null;
  };
  const [hover, setHover] = useState<HoverContent | null>(null);
  const [widthPx, setWidthPx] = useState(0);

  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    setWidthPx(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidthPx(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const showCrosshair = (pct: number, x: number, chartWidthPx: number) => {
    if (crosshairRef.current) {
      const cx = String(pct * 100);
      crosshairRef.current.setAttribute('x1', cx);
      crosshairRef.current.setAttribute('x2', cx);
      crosshairRef.current.style.visibility = 'visible';
    }
    if (tooltipRef.current) {
      tooltipRef.current.style.visibility = 'visible';
      // Tooltip uses `transform: translateX(-50%)` so style.left places
      // its centre at x. Clamp so the visible box stays inside the chart
      // even when the cursor is near either edge.
      const halfW = tooltipRef.current.offsetWidth / 2;
      const clampedX =
        halfW > 0
          ? Math.max(halfW, Math.min(chartWidthPx - halfW, x))
          : x;
      tooltipRef.current.style.left = `${clampedX}px`;
    }
  };

  const hideCrosshair = () => {
    if (crosshairRef.current) crosshairRef.current.style.visibility = 'hidden';
    if (tooltipRef.current) tooltipRef.current.style.visibility = 'hidden';
  };

  const showBrush = (downPct: number, currPct: number) => {
    if (!brushRectRef.current) return;
    const left = Math.min(downPct, currPct) * 100;
    const width = Math.abs(currPct - downPct) * 100;
    brushRectRef.current.setAttribute('x', String(left));
    brushRectRef.current.setAttribute('width', String(width));
    brushRectRef.current.style.visibility = 'visible';
  };

  const hideBrush = () => {
    if (brushRectRef.current) brushRectRef.current.style.visibility = 'hidden';
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const x = e.clientX - rect.left;
    pointerDownXRef.current = x;
    dragStateRef.current = 'maybe-drag';
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const x = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));

    if (dragStateRef.current === 'dragging') {
      const downPct = pointerDownXRef.current / rect.width;
      showBrush(downPct, pct);
      return;
    }
    if (dragStateRef.current === 'maybe-drag') {
      if (Math.abs(x - pointerDownXRef.current) >= DRAG_THRESHOLD_PX) {
        dragStateRef.current = 'dragging';
        hideCrosshair();
        const downPct = pointerDownXRef.current / rect.width;
        showBrush(downPct, pct);
        return;
      }
    }

    showCrosshair(pct, x, rect.width);
    const tMs = sinceMs + pct * spanMs;
    // Tolerance for nearest-event lookups: 1 % of the visible span, or
    // 30 s minimum so a single error 5 s wide still snaps.
    const tolMs = Math.max(spanMs * 0.01, 30_000);

    // Per-series values at tMs — pick the nearest bucket within the
    // tolerance. Linear scan is fine at ≤ ~2000 points per row.
    const nearestPoint = (() => {
      if (allPoints.length === 0) return null;
      let best: TelemetryPoint | null = null;
      let bestDist = Infinity;
      for (const p of allPoints) {
        const d = Math.abs(Date.parse(p.bucket) - tMs);
        if (d < bestDist) {
          bestDist = d;
          best = p;
        }
      }
      // Reject distant matches so the tooltip doesn't claim there's a
      // value at a time well outside the buckets we have.
      return bestDist <= tolMs * 5 ? best : null;
    })();
    const perSeries = visibleSeries.map((s) => ({
      series: s,
      value: nearestPoint ? s.accessor(nearestPoint) : null,
    }));
    const throttled = nearestPoint?.throttled_any === true;

    let nearestBootEv: StateHistoryEvent | null = null;
    let nearestBootDelta = Infinity;
    for (const ev of bootMarkers) {
      const d = Math.abs(new Date(ev.ts).getTime() - tMs);
      if (d < nearestBootDelta && d <= tolMs) {
        nearestBootDelta = d;
        nearestBootEv = ev;
      }
    }
    setHover({
      tMs,
      perSeries,
      throttled,
      nearestBoot: nearestBootEv
        ? {
            ts: new Date(nearestBootEv.ts).getTime(),
            fw: String((nearestBootEv.data as { fw_ver?: unknown }).fw_ver ?? '?'),
            deltaMs: nearestBootDelta,
          }
        : null,
    });
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) {
      dragStateRef.current = 'idle';
      return;
    }
    const upX = e.clientX - rect.left;
    const wasDrag = dragStateRef.current === 'dragging';
    dragStateRef.current = 'idle';
    e.currentTarget.releasePointerCapture(e.pointerId);

    if (wasDrag) {
      const downX = pointerDownXRef.current;
      const x0 = Math.min(downX, upX);
      const x1 = Math.max(downX, upX);
      const t0 = sinceMs + (x0 / rect.width) * spanMs;
      const t1 = sinceMs + (x1 / rect.width) * spanMs;
      const newCentre = (t0 + t1) / 2;
      const newSpan = t1 - t0;
      hideBrush();
      setCustomWindow(newCentre, newSpan);
    }
  };

  const handlePointerLeave = () => {
    if (dragStateRef.current === 'dragging') {
      hideBrush();
    }
    dragStateRef.current = 'idle';
    hideCrosshair();
    setHover(null);
  };

  // Group visible series by section for the row stack render.
  const sectionGroups = useMemo(() => {
    const groups: Array<{ section: TelemetrySection; series: TelemetrySeries[] }> = [];
    for (const section of TELEMETRY_SECTIONS) {
      const series = visibleSeries.filter((s) => s.section === section);
      if (series.length > 0) groups.push({ section, series });
    }
    return groups;
  }, [visibleSeries]);

  // Compute pixel-X for boot markers so the click-to-popover handler can
  // accept clicks directly on the tick element (avoids fragile pointer
  // hit-testing against the overlay).
  const span = Math.max(untilMs - sinceMs, 1);
  const xPct = (ms: number) => ((ms - sinceMs) / span) * 100;
  const bootsInWindow = useMemo(
    () =>
      bootMarkers
        .map((ev) => ({
          ev,
          ts: new Date(ev.ts).getTime(),
        }))
        .filter((b) => b.ts >= sinceMs && b.ts <= untilMs),
    [bootMarkers, sinceMs, untilMs],
  );

  // Throttled buckets in the visible window — used by the dedicated
  // tick strip below the CPU row.
  const throttledBuckets = useMemo(
    () =>
      points
        .filter((p) => p.throttled_any === true)
        .map((p) => Date.parse(p.bucket)),
    [points],
  );

  const axisTicks = useMemo(
    () =>
      [0, 0.25, 0.5, 0.75, 1].map((t) => ({
        pct: t * 100,
        label: fmtAxisTick(sinceMs + t * span, spanMs / 3_600_000),
      })),
    [sinceMs, span, spanMs],
  );

  const isLoading = telemetryQ.isLoading && allPoints.length === 0;
  const noDataInWindow = !isLoading && points.length === 0;

  return (
    <section className="card space-y-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-medium">Telemetry history</h2>
          <p className="text-[10px] text-muted tabular-nums">
            {fmtWindowEdge(sinceMs, spanMs / 3_600_000)}
            {' → '}
            {fmtWindowEdge(untilMs, spanMs / 3_600_000)}
            <span className="mx-1.5">·</span>
            {bucket} buckets
            <span className="mx-1.5">·</span>
            {points.length} {points.length === 1 ? 'sample' : 'samples'}
          </p>
        </div>
        <TelemetryRangeBar
          rangeKey={rangeKey}
          isLive={isLive}
          untilMs={untilMs}
          nowMs={nowMs}
          halfSpanMs={halfSpanMs}
          isCustomSpan={isCustomSpan}
          setRangeKey={setRangeKey}
          setCentreMs={setCentreMs}
          resetCustom={resetCustom}
        />
      </div>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {!isLoading && noDataInWindow && (
        <p className="text-sm text-muted">
          No telemetry in this window. The {bucket} matview refreshes on its
          own cadence; try a wider range or wait for the next publish.
        </p>
      )}

      {!isLoading && points.length > 0 && (
        <div className="relative" ref={chartContainerRef}>
          {sectionGroups.map(({ section, series }) => (
            <SectionGrid
              key={section}
              section={section}
              series={series}
              points={points}
              sinceMs={sinceMs}
              spanMs={spanMs}
              axisTicks={axisTicks}
            />
          ))}

          {/* Annotation strips — throttled + boot. Use the same 3-col
              grid so they line up with the chart column above. */}
          <div
            className="grid items-center pt-2"
            style={{
              gridTemplateColumns: `${LABEL_COL_PX}px ${YTICK_COL_PX}px 1fr`,
              columnGap: GRID_GAP_PX,
              rowGap: 4,
            }}
          >
            <div className="text-[10px] text-muted">
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-[2px] h-2.5 bg-red-600" />
                Throttled
              </span>
            </div>
            <div />
            <div className="relative" style={{ height: STRIP_HEIGHT }}>
              <svg
                width="100%"
                height={STRIP_HEIGHT}
                viewBox={`0 0 ${VIEWBOX_W} ${STRIP_HEIGHT}`}
                preserveAspectRatio="none"
              >
                {throttledBuckets.map((ts, i) => (
                  <line
                    key={i}
                    x1={xPct(ts)}
                    x2={xPct(ts)}
                    y1="2"
                    y2={STRIP_HEIGHT - 2}
                    stroke="#dc2626"
                    strokeWidth="0.4"
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
              </svg>
            </div>

            <div className="text-[10px] text-muted">
              <span className="inline-flex items-center gap-1">
                <span
                  className="inline-block w-[2px] h-2.5"
                  style={{
                    background:
                      'repeating-linear-gradient(to bottom,#0d9488 0 2px,transparent 2px 4px)',
                  }}
                />
                Boot
              </span>
            </div>
            <div />
            <div className="relative" style={{ height: STRIP_HEIGHT }}>
              <svg
                width="100%"
                height={STRIP_HEIGHT}
                viewBox={`0 0 ${VIEWBOX_W} ${STRIP_HEIGHT}`}
                preserveAspectRatio="none"
              >
                {bootsInWindow.map(({ ev, ts }) => (
                  <line
                    key={ev.event_id}
                    x1={xPct(ts)}
                    x2={xPct(ts)}
                    y1="2"
                    y2={STRIP_HEIGHT - 2}
                    stroke="#0d9488"
                    strokeWidth="0.4"
                    strokeDasharray="0.6 0.6"
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
              </svg>
            </div>
          </div>

          {/* Overlay layer — captures pointer events for the whole
              chart column, spanning every section grid + the annotation
              strips. Positioned absolutely with offset matching the
              label + y-tick columns + their two grid gaps. */}
          <div
            ref={overlayRef}
            className="absolute top-0 bottom-0 cursor-crosshair touch-none"
            style={{
              left: CHART_COL_OFFSET_PX,
              right: 0,
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerLeave}
            onPointerCancel={handlePointerLeave}
          >
            <svg
              width="100%"
              height="100%"
              viewBox={`0 0 ${VIEWBOX_W} 100`}
              preserveAspectRatio="none"
              className="absolute inset-0 pointer-events-none"
            >
              <rect
                ref={brushRectRef}
                y="0"
                height="100"
                fill="#00B4D0"
                fillOpacity="0.12"
                stroke="#00B4D0"
                strokeWidth="0.3"
                vectorEffect="non-scaling-stroke"
                style={{ visibility: 'hidden' }}
              />
              <line
                ref={crosshairRef}
                y1="0"
                y2="100"
                stroke="#1C1B1F"
                strokeWidth="0.3"
                strokeDasharray="0.6 0.4"
                vectorEffect="non-scaling-stroke"
                style={{ visibility: 'hidden' }}
              />
            </svg>

            <TelemetryTooltip
              hover={hover}
              tooltipRef={tooltipRef}
              widthPx={widthPx}
              spanMs={spanMs}
            />
          </div>
        </div>
      )}

    </section>
  );
}

// ─── Section grid ───────────────────────────────────────────────────────

/**
 * One section (Host / Network / Sensors / Projector) renders as its own
 * 3-column CSS grid:
 *
 *   [ label 108px ] [ y-tick 44px ] [ chart 1fr ]
 *
 * Section header spans all three columns; each TelemetryRow emits three
 * cells (label, y-tick, chart). The bottom of every section grid is its
 * own X-axis tick row, so time labels are never more than ~4 chart rows
 * away from any reading.
 *
 * The synchronized crosshair + brush + tooltip overlay is positioned
 * absolutely over every section grid in the chart container, so all
 * sections share a single pointer interaction even though they're
 * rendered as separate grids.
 */
function SectionGrid({
  section,
  series,
  points,
  sinceMs,
  spanMs,
  axisTicks,
}: {
  section: TelemetrySection;
  series: TelemetrySeries[];
  points: TelemetryPoint[];
  sinceMs: number;
  spanMs: number;
  axisTicks: Array<{ pct: number; label: string }>;
}) {
  return (
    <div
      className="grid items-center pt-3"
      style={{
        gridTemplateColumns: `${LABEL_COL_PX}px ${YTICK_COL_PX}px 1fr`,
        columnGap: GRID_GAP_PX,
        rowGap: 4,
      }}
    >
      <div className="col-span-3 text-[10px] font-medium text-muted uppercase tracking-wider pb-1">
        {section}
      </div>
      {series.map((s) => (
        <TelemetryRow
          key={s.id}
          series={s}
          points={points}
          sinceMs={sinceMs}
          spanMs={spanMs}
          height={ROW_HEIGHT}
        />
      ))}
      {/* Per-section X-axis row — two empty cells then the tick labels
          under the chart column. */}
      <div />
      <div />
      <div className="relative h-4 text-[10px] text-muted">
        {axisTicks.map((t, i) => (
          <span
            key={i}
            className="absolute tabular-nums"
            style={{
              left: `${t.pct}%`,
              transform:
                i === 0
                  ? 'translateX(0%)'
                  : i === axisTicks.length - 1
                    ? 'translateX(-100%)'
                    : 'translateX(-50%)',
            }}
          >
            {t.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Tooltip ────────────────────────────────────────────────────────────

function TelemetryTooltip({
  hover,
  tooltipRef,
  spanMs,
}: {
  hover:
    | {
        tMs: number;
        perSeries: Array<{ series: TelemetrySeries; value: number | null }>;
        throttled: boolean;
        nearestBoot: { ts: number; fw: string; deltaMs: number } | null;
      }
    | null;
  tooltipRef: React.RefObject<HTMLDivElement>;
  widthPx: number;
  spanMs: number;
}) {
  return (
    <div
      ref={tooltipRef}
      role="tooltip"
      className="absolute z-20 pointer-events-none -translate-x-1/2 -translate-y-full bg-white border border-border rounded-md shadow-lg px-2.5 py-1.5 text-[10px] text-fg tabular-nums whitespace-nowrap"
      style={{ visibility: 'hidden', top: -4, left: 0 }}
    >
      {hover && (
        <>
          <div className="font-medium mb-1">
            {fmtTooltipTime(hover.tMs, spanMs / 3_600_000)}
          </div>
          <div className="flex flex-col gap-0.5">
            {hover.perSeries.map(({ series, value }) => (
              <div key={series.id} className="flex items-center gap-1.5">
                <span
                  className="inline-block w-2 h-2 rounded-sm"
                  style={{ backgroundColor: series.color }}
                  aria-hidden="true"
                />
                <span className="text-muted">{series.label}:</span>
                <span>{value === null ? '—' : series.format(value)}</span>
              </div>
            ))}
            {hover.throttled && (
              <div className="flex items-center gap-1.5 pt-1 mt-0.5 border-t border-border">
                <span className="inline-block w-[2px] h-2.5 bg-red-600" />
                <span className="text-muted">CPU throttled this bucket</span>
              </div>
            )}
            {hover.nearestBoot && (
              <div className="flex items-center gap-1.5 pt-1 mt-0.5 border-t border-border">
                <span
                  className="inline-block w-[2px] h-2.5"
                  style={{
                    background:
                      'repeating-linear-gradient(to bottom,#0d9488 0 2px,transparent 2px 4px)',
                  }}
                />
                <span className="text-muted">
                  boot {fmtOffsetAgo(hover.nearestBoot.deltaMs)}{' '}
                  {hover.nearestBoot.ts <= hover.tMs ? 'ago' : 'ahead'}:
                </span>
                <span>fw {hover.nearestBoot.fw}</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function fmtTooltipTime(ms: number, hours: number): string {
  const d = new Date(ms);
  if (hours <= 36) {
    return d.toLocaleString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }
  return d.toLocaleString([], {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
